"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import PageHeader from "@/components/layout/PageHeader";
import ViewToggle, { useListView } from "@/components/crm/ViewToggle";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import { useCounts, countText } from "@/components/crm/useCounts";
import { Avatar } from "@/components/crm/ListShell";
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  Trash2,
  Building2,
  Phone,
  Mail,
  User,
  Calendar,
  DollarSign,
  Save,
  Edit,
  Plus,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post, can } from "../utils/service";
import FloatingDrawer from "@/components/crm/FloatingDrawer";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Contract {
  id: string;
  number?: string | null;
  title?: string | null;
  account_id: string;
  service_type: string;
  start_date: string;
  duration_months: number;
  end_date: string;
  value: string | number | null;
  currency: string;
  billing_cycle: string;
  status: string;
  auto_renew: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  periods_missing?: number;
  billing_mode?: string;
}

interface Client {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string | null;
  industry: string | null;
  status: string;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  owner_name?: string;
  primary_contact_id?: string | null;
  contracts?: Contract[];
}

interface ApiAccount {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  industry: string | null;
  status: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  owner?: { name: string } | null;
  primary_contact?: { id: string; name: string; email: string | null; phone: string | null } | null;
}

/** Maps an account (and its contracts) onto the fields this page displays. */
const fromApi = (a: ApiAccount, contracts: Contract[]): Client => ({
  id: a.id,
  company_name: a.name,
  contact_person: a.primary_contact?.name ?? "",
  email: a.email || a.primary_contact?.email || "",
  phone: a.phone || a.primary_contact?.phone || null,
  industry: a.industry,
  status: a.status,
  notes: a.description,
  created_by: a.created_by ?? "",
  created_at: a.created_at,
  updated_at: a.updated_at,
  owner_name: a.owner?.name ?? "",
  primary_contact_id: a.primary_contact?.id ?? null,
  contracts: contracts.filter((c) => c.account_id === a.id),
});

const apiMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
  return (errors && Object.values(errors).flat()[0]) || error?.response?.data?.message || fallback;
};

const splitName = (full: string) => {
  const [first, ...rest] = full.trim().split(/\s+/);
  return { first_name: first || "", last_name: rest.join(" ") || null };
};

interface ClientsResponse {
  success: boolean;
  message: string;
  data: ApiAccount[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const CLIENT_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
};

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  ended: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
  renewed: "bg-blue-100 text-blue-800",
};

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
  if (amount === null || amount === undefined) return `${currency} 0`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return `${currency} 0`;
  if (numAmount >= 1000000000) return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
  if (numAmount >= 1000000) return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
  return `${currency} ${numAmount.toLocaleString()}`;
};

const isExpiringSoon = (contract: Contract) => {
  if (contract.status !== "active") return false;
  const end = new Date(contract.end_date).getTime();
  const now = Date.now();
  const diffDays = (end - now) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 30;
};

const nearestEndDate = (contracts?: Contract[]) => {
  if (!contracts || contracts.length === 0) return null;
  const active = contracts.filter((c) => c.status === "active");
  if (active.length === 0) return null;
  return active.reduce((soonest, c) =>
    new Date(c.end_date) < new Date(soonest.end_date) ? c : soonest
  );
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);


interface LeadSearchResult {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string | null;
  industry: string | null;
  value: string | number | null;
  currency: string;
}

const AddClientForm = ({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"new" | "existing_lead">("new");
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    industry: "",
    status: "active",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // "From Existing Lead" mode
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState<LeadSearchResult[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadSearchResult | null>(null);

  useEffect(() => {
    if (mode !== "existing_lead" || !leadSearch.trim()) {
      setLeadResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setIsSearchingLeads(true);
      try {
        const response = await get<{ success: boolean; data: Array<{ id: string; name: string; company_name: string | null; first_name: string | null; last_name: string | null; email: string | null; phone: string | null; industry: string | null; estimated_value: string | null; currency: string }> }>(
          `/leads?search=${encodeURIComponent(leadSearch)}&status=new,contacted,qualified,unqualified,lost&page_size=8`
        );
        if (response.success) {
          setLeadResults(response.data.map((l) => ({
            id: l.id,
            company_name: l.company_name || l.name,
            contact_person: [l.first_name, l.last_name].filter(Boolean).join(" "),
            email: l.email ?? "",
            phone: l.phone,
            industry: l.industry,
            value: l.estimated_value,
            currency: l.currency,
          })));
        }
      } catch (error) {
        console.error("Error searching leads:", error);
      } finally {
        setIsSearchingLeads(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [leadSearch, mode]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ company_name: "", contact_person: "", email: "", phone: "", industry: "", status: "active", notes: "" });
    setMode("new");
    setLeadSearch("");
    setLeadResults([]);
    setSelectedLead(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (mode === "existing_lead") {
        if (!selectedLead) return;
        // Conversion creates the account/contact; the dedicated page collects the full agreement setup.
        const converted = await post<{ data: { account: { id: string } } }>(`/leads/${selectedLead.id}/convert`, {});
        onSuccess(); onClose(); resetForm();
        if (can("contracts.create")) navigate(`/contracts/new?account_id=${converted.data.account.id}&currency=${encodeURIComponent(selectedLead.currency)}`);
        return;
      } else {
        const created = await post<{ data: { account: { id: string } } }>("/accounts", {
          name: formData.company_name,
          type: "customer",
          email: formData.email || null,
          phone: formData.phone || null,
          industry: formData.industry || null,
          status: formData.status,
          description: formData.notes || null,
        });
        if (formData.contact_person.trim()) {
          await post("/contacts", {
            ...splitName(formData.contact_person),
            account_id: created.data.account.id,
            email: formData.email || null,
            phone: formData.phone || null,
            is_primary: true,
          });
        }
      }
      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      notify(apiMessage(error, "Could not create the account."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add New Account</h2>
          <p className="text-xs text-gray-400 mt-1">A customer company and its main contact</p>
        </div>
      </div>

      <div className="px-6 pt-4">
        <div className="flex gap-2 bg-gray-900 border border-gray-700 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              mode === "new" ? "bg-primary text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            New Client
          </button>
          <button
            type="button"
            onClick={() => setMode("existing_lead")}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              mode === "existing_lead" ? "bg-primary text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            From Existing Lead
          </button>
        </div>
      </div>

      {mode === "existing_lead" ? (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <p className="text-xs text-gray-400">
            Search for a lead already in the system — even an old one — and convert it into a client with its first contract.
          </p>

          {!selectedLead ? (
            <>
              <div>
                <label className="text-xs font-semibold text-white mb-2 block">Search Leads *</label>
                <SearchInput
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search by company, contact, or email..."
                  className="w-full" dark
                />
              </div>

              {isSearchingLeads && <p className="text-xs text-gray-500">Searching...</p>}

              {leadResults.length > 0 && (
                <div className="space-y-2">
                  {leadResults.map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => setSelectedLead(lead)}
                      className="w-full text-left bg-gray-900 border border-gray-800 rounded-lg p-3 hover:border-primary transition-colors"
                    >
                      <p className="text-xs font-semibold text-white">{lead.company_name}</p>
                      <p className="text-xs text-gray-400">{lead.contact_person} · {lead.email}</p>
                    </button>
                  ))}
                </div>
              )}

              {leadSearch.trim() && !isSearchingLeads && leadResults.length === 0 && (
                <p className="text-xs text-gray-500">No matching leads found.</p>
              )}
            </>
          ) : (
            <>
              <div className="bg-gray-900 rounded-lg p-4 flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-white">{selectedLead.company_name}</p>
                  <p className="text-xs text-gray-400 mt-1">{selectedLead.contact_person} · {selectedLead.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedLead(null)}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Change
                </button>
              </div>

              {can("contracts.create") && <p className="text-xs text-gray-400">After conversion, continue to the contract page to set up agreement terms, renewal policy, deliverable rates and invoice settings.</p>}
            </>
          )}
        </form>
      ) : (
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Company Name *</label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Contact Person *</label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Industry</label>
          <input
            type="text"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <AppSelect
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </AppSelect>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>
      )}

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            (mode === "new"
              ? !formData.company_name || !formData.contact_person || !formData.email
              : !selectedLead)
          }
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {mode === "existing_lead" ? "Converting..." : "Creating..."}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {mode === "existing_lead" ? "Convert to Account" : "Create Account"}
            </>
          )}
        </button>
        <button
          onClick={() => {
            resetForm();
            onClose();
          }}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditClientForm = ({
  isOpen,
  onClose,
  onSuccess,
  client,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client: Client | null;
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    industry: "",
    status: "active",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        company_name: client.company_name,
        contact_person: client.contact_person,
        email: client.email,
        phone: client.phone || "",
        industry: client.industry || "",
        status: client.status,
        notes: client.notes || "",
      });
    }
  }, [client]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    setIsSubmitting(true);
    try {
      await put(`/accounts/${client.id}`, {
        name: formData.company_name,
        email: formData.email || null,
        phone: formData.phone || null,
        industry: formData.industry || null,
        status: formData.status,
        description: formData.notes || null,
      });
      // The contact person is the account's primary contact.
      if (formData.contact_person.trim() && formData.contact_person.trim() !== client.contact_person) {
        if (client.primary_contact_id) {
          await put(`/contacts/${client.primary_contact_id}`, splitName(formData.contact_person));
        } else {
          await post("/contacts", { ...splitName(formData.contact_person), account_id: client.id, email: formData.email || null, phone: formData.phone || null, is_primary: true });
        }
      }
      onSuccess();
      onClose();
    } catch (error) {
      notify(apiMessage(error, "Could not update the account."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!client) return null;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Client</h2>
          <p className="text-xs text-gray-400 mt-1">Update client details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Company Name *</label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Contact Person *</label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Industry</label>
          <input
            type="text"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <AppSelect
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </AppSelect>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update Client
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const ClientDrawer = ({
  client,
  isOpen,
  onClose,
  onDelete,
  onEdit,
  onAddContract,
  onDeleteContract,
}: {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (clientId: string) => void;
  onEdit: () => void;
  onAddContract: () => void;
  onDeleteContract: (contractId: string) => void;
}) => {
  if (!client) return null;

  const contracts = client.contracts || [];

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Account Details</h2>
          <p className="text-xs text-gray-400 mt-1">{client.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <div className={`inline-block px-3 py-1 text-xs font-semibold rounded-lg ${CLIENT_STATUS_COLORS[client.status] || "bg-gray-100 text-gray-800"}`}>
            {formatLabel(client.status)}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Company</p>
              <p className="text-xs text-gray-400">{client.company_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Contact Person</p>
              <p className="text-xs text-gray-400">{client.contact_person}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{client.email}</p>
            </div>
          </div>

          {client.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Phone</p>
                <p className="text-xs text-gray-400">{client.phone}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(client.created_at).toLocaleDateString()}
                {client.owner_name ? ` · owned by ${client.owner_name}` : ""}
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Contracts ({contracts.length})
            </label>
            {can("contracts.create") && <button
              onClick={onAddContract}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>}
          </div>

          {contracts.length === 0 ? (
            <p className="text-xs text-gray-500">No contracts yet.</p>
          ) : (
            <div className="space-y-3">
              {contracts.map((contract) => {
                const expiringSoon = isExpiringSoon(contract);
                return (
                  <div key={contract.id} className="bg-gray-900 rounded-lg p-3 border border-gray-800">
                    <div className="flex items-start justify-between mb-2">
                      <Link to={`/contracts/${contract.id}`} className="block text-xs font-semibold text-white hover:text-primary">{contract.number ? `${contract.number} · ` : ""}{contract.title || formatLabel(contract.service_type)}</Link>
                      <div className="flex items-center gap-1">
                        {expiringSoon && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <AlertCircle className="w-3 h-3" />
                            Expiring soon
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONTRACT_STATUS_COLORS[contract.status] || "bg-gray-100 text-gray-800"}`}>
                          {formatLabel(contract.status)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(contract.start_date).toLocaleDateString()} → {new Date(contract.end_date).toLocaleDateString()} ({contract.duration_months}mo)
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">
                        {contract.billing_mode === "variable" ? "Based on delivered quantities" : contract.billing_mode === "percentage" ? `${formatCurrency(contract.value, contract.currency)} total · percentage instalments` : `${formatCurrency(contract.value, contract.currency)} / ${formatLabel(contract.billing_cycle)}`}
                        {contract.auto_renew && (
                          <span className="ml-2 inline-flex items-center gap-1 text-blue-400">
                            <RefreshCw className="w-3 h-3" /> auto-renew
                          </span>
                        )}
                      </p>
                      <button
                        onClick={async () => {
                          if (await confirmDialog("Delete this contract?")) onDeleteContract(contract.id);
                        }}
                        className="text-red-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {contract.billing_mode === "percentage" && can("invoices.create") && can("contracts.view") && <Link to={`/contracts/${contract.id}?generate=1`} className="mt-2 block text-xs text-primary hover:text-primary/80">Generate percentage invoice →</Link>}
                    {(contract.periods_missing ?? 0) > 0 && (
                      <Link
                        to={`/invoices/client/${client.id}?generate=${contract.id}`}
                        className="mt-3 block rounded-lg bg-primary px-3 py-2 text-center text-xs font-medium text-white hover:opacity-90 transition"
                      >
                        Generate {contract.periods_missing} backdated invoice{contract.periods_missing === 1 ? "" : "s"}
                      </Link>
                    )}
                    <Link
                      to={`/invoices/client/${client.id}`}
                      className="mt-2 inline-block text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      View client invoices →
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 border-t border-gray-800">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={async () => {
              if (await confirmDialog("Are you sure you want to delete this account?")) {
                onDelete(client.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { client: Client; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <Edit className="w-3 h-3" />
            Edit Account
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [view, setView] = useListView("accounts", ["table", "cards"] as const, "table");
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [sort, setSort] = useState("name");
  const [statsKey, setStatsKey] = useState(0);
  useEffect(() => { const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const { counts, loading: countsLoading } = useCounts("/accounts", {
    all: {}, customers: { type: "customer" }, prospects: { type: "prospect" }, inactive: { status: "inactive" },
  }, statsKey);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientDrawer, setShowClientDrawer] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0,
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<ClientsResponse>("/accounts", {
        page: pagination.page,
        page_size: pagination.per_page,
        status: statusFilter !== "all" ? statusFilter : undefined,
        type: typeFilter || undefined,
        search: searchQuery || undefined,
        sort,
      });
      setStatsKey((k) => k + 1);
      if (response.success) {
        // Contracts of the accounts on this page, in one request.
        const ids = response.data.map((a) => a.id);
        const contracts = ids.length
          ? (await get<{ data: Contract[] }>(`/contracts?account_id=${ids.join(",")}&page_size=100`)).data
          : [];
        const rows = response.data.map((a) => fromApi(a, contracts));
        setClients(rows);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
        if (selectedClient) {
          const refreshed = rows.find((c) => c.id === selectedClient.id);
          if (refreshed) setSelectedClient(refreshed);
        }
      }
    } catch (error) {
      notify(apiMessage(error, "Could not load accounts."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page, statusFilter, typeFilter, searchQuery, sort]);

  // New filters start again from the first page.
  useEffect(() => { setPagination((prev) => ({ ...prev, page: 1 })); }, [statusFilter, typeFilter, searchQuery, sort]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected accounts? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/accounts/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setClients((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} could not be deleted (accounts with invoices or contracts are kept)` : `${deleted.size} accounts deleted`);
  };

  const handleDelete = async (clientId: string) => {
    try {
      await del(`/accounts/${clientId}`);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      if (selectedClient?.id === clientId) {
        setShowClientDrawer(false);
        setSelectedClient(null);
      }
      notify("Account deleted");
    } catch (error) {
      notify(apiMessage(error, "Could not delete the account."));
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    if (!selectedClient) return;
    try {
      await del(`/contracts/${contractId}`);
      notify("Contract deleted");
      fetchClients();
    } catch (error) {
      notify(apiMessage(error, "Could not delete the contract."));
    }
  };

  const filteredClients = clients; // search and filters are applied by the API


  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowClientDrawer(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setShowEditClient(true);
  };

  const handleAddSuccess = () => {
    fetchClients();
    notify("Account created");
  };

  const handleEditSuccess = () => {
    fetchClients();
    notify("Account updated");
  };

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: "company_name",
      header: "Account",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.company_name}</p>
          <p className="text-xs text-gray-600">{row.original.contact_person}</p>
        </div>
      ),
    },
    {
      accessorKey: "industry",
      header: "Industry",
      cell: ({ row }) => (
        <p className="text-xs text-gray-700">{row.original.industry ? formatLabel(row.original.industry) : "—"}</p>
      ),
    },
    {
      id: "contracts",
      header: "Contracts",
      cell: ({ row }) => {
        const contracts = row.original.contracts || [];
        const nearest = nearestEndDate(contracts);
        return (
          <div>
            <p className="text-xs text-gray-700">{contracts.length} total</p>
            {nearest && (
              <p className={`text-xs ${isExpiringSoon(nearest) ? "text-amber-600 font-semibold" : "text-gray-500"}`}>
                Next end: {new Date(nearest.end_date).toLocaleDateString()}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CLIENT_STATUS_COLORS[row.original.status] || "bg-gray-100 text-gray-800"}`}>
          {formatLabel(row.original.status)}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <p className="text-xs text-gray-600">{new Date(row.original.created_at).toLocaleDateString()}</p>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          client={row.original}
          onView={() => handleViewClient(row.original)}
          onEdit={() => handleEditClient(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Accounts" description="Customers, their contacts and service contracts">
        </PageHeader>

        <GridProvider id="accounts">
        <div className="space-y-4">
        <GridToolbar
          view={<ViewToggle view={view} onChange={setView} options={[{ id: "table", label: "Table" }, { id: "cards", label: "Cards" }]} />}
          search={<SearchInput className="w-full sm:w-64" placeholder="Search name, email, phone or city…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />}
          filters={<>
            <label className="text-xs text-gray-600">Status
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All accounts</option><option value="active">Active</option><option value="inactive">Inactive</option>
              </AppSelect>
            </label>
            <label className="text-xs text-gray-600">Type
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">Any type</option><option value="customer">Customer</option><option value="prospect">Prospect</option><option value="partner">Partner</option><option value="vendor">Vendor</option><option value="other">Other</option>
              </AppSelect>
            </label>
          </>}
          activeFilters={(statusFilter !== "all" ? 1 : 0) + (typeFilter ? 1 : 0)}
          onClearFilters={() => { setStatusFilter("all"); setTypeFilter(""); }}
          sortOptions={[{ value: "name", label: "Name" }, { value: "-created_at", label: "Newest" }, { value: "created_at", label: "Oldest" }, { value: "-updated_at", label: "Recently updated" }, { value: "type", label: "Type" }]}
          sort={sort}
          onSort={setSort}
          hasStats
          exportEntity="accounts"
          exportPermission="accounts.export"
          onAdd={() => setShowAddClient(true)}
          addLabel="Add Account"
        />
        <StatsStrip loading={countsLoading} items={[
          { label: "All accounts", value: countText(counts.all) },
          { label: "Customers", value: countText(counts.customers), tone: "good" },
          { label: "Prospects", value: countText(counts.prospects) },
          { label: "Inactive", value: countText(counts.inactive) },
        ]} />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              {view === "cards" ? (
                filteredClients.length > 0 && (
                  <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {filteredClients.map((c) => {
                      const contracts = c.contracts || [];
                      const nearest = nearestEndDate(contracts);
                      return (
                        <div key={c.id} onClick={() => handleViewClient(c)} className="flex cursor-pointer flex-col rounded-xl border border-gray-200 bg-white p-4 transition hover:border-primary/40 hover:shadow-sm">
                          <div className="flex items-start gap-3">
                            <Avatar name={c.company_name} className="rounded-lg" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-gray-900">{c.company_name}</p>
                              <p className="truncate text-xs text-gray-500">{[c.industry ? formatLabel(c.industry) : null, c.contact_person].filter(Boolean).join(" · ") || "No contact yet"}</p>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CLIENT_STATUS_COLORS[c.status] || "bg-gray-100 text-gray-800"}`}>{formatLabel(c.status)}</span>
                          </div>
                          <div className="mt-4 space-y-1 text-xs text-gray-600">
                            {c.email && <p className="truncate">{c.email}</p>}
                            {c.phone && <p>{c.phone}</p>}
                          </div>
                          <div className="min-h-[1rem] flex-1" />
                          <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
                            <span>{contracts.length} contract{contracts.length === 1 ? "" : "s"}</span>
                            {nearest ? (
                              <span className={isExpiringSoon(nearest) ? "font-semibold text-amber-600" : ""}>Next end {new Date(nearest.end_date).toLocaleDateString()}</span>
                            ) : <span>{c.owner_name ?? ""}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
              <TableProvider columns={columns} data={filteredClients} onDeleteSelected={handleBatchDelete}>
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>
              )}

              {filteredClients.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No accounts yet</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" || typeFilter
                      ? "Adjust your search criteria or filters to find relevant accounts."
                      : "Add your customers here, or convert a lead into an account."}
                  </p>
                  {!searchQuery && statusFilter === "all" && !typeFilter && (
                    <button
                      type="button"
                      onClick={() => setShowAddClient(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Add First Account
                    </button>
                  )}
                </div>
              )}

            </>
          )}
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.total_pages}
            total={pagination.total}
            perPage={pagination.per_page}
            onPage={handlePageChange}
            onPerPage={(n) => setPagination((prev) => ({ ...prev, per_page: n, page: 1 }))}
            noun="accounts"
          />
        </div>
        </div>
        </GridProvider>
      </div>

      <ClientDrawer
        client={selectedClient}
        isOpen={showClientDrawer}
        onClose={() => {
          setShowClientDrawer(false);
          setSelectedClient(null);
        }}
        onDelete={handleDelete}
        onEdit={() => {
          setShowClientDrawer(false);
          setShowEditClient(true);
        }}
        onAddContract={() => { if (selectedClient) navigate(`/contracts/new?account_id=${selectedClient.id}`); }}
        onDeleteContract={handleDeleteContract}
      />

      <AddClientForm isOpen={showAddClient} onClose={() => setShowAddClient(false)} onSuccess={handleAddSuccess} />

      <EditClientForm
        isOpen={showEditClient}
        onClose={() => {
          setShowEditClient(false);
        }}
        onSuccess={handleEditSuccess}
        client={selectedClient}
      />


    </div>
  );
}

export default ClientsPage;
